import { apiFetch as fetch } from "../lib/security";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Ban, Bell, CalendarCheck, CalendarOff, Check, Clock, Download, Inbox, LockKeyhole, LogOut, Mail, MessageCircle, RefreshCcw, Send, ShieldCheck, Tag, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { SEO } from "../components/SEO";
import { apartments } from "../data/apartments";
import { siteInfo } from "../data/site";

const adminStorageKey = "depositSeedHouse.adminToken";
const bookingTimeZone = "America/New_York";

const adminNavItems = [
  { href: "#admin-chat", label: "Chat" },
  { href: "/admin/chat-settings", label: "Chat settings" },
  { href: "#admin-inquiries", label: "Inquiries" },
  { href: "#admin-email", label: "Email setup" },
  { href: "#admin-bookings", label: "Bookings" },
  { href: "#admin-blocks", label: "Blocked dates" },
  { href: "#admin-marketing", label: "Marketing" },
];

const chatWeekdays = [
  { id: "monday", label: "Monday" },
  { id: "tuesday", label: "Tuesday" },
  { id: "wednesday", label: "Wednesday" },
  { id: "thursday", label: "Thursday" },
  { id: "friday", label: "Friday" },
  { id: "saturday", label: "Saturday" },
  { id: "sunday", label: "Sunday" },
] as const;

type BookingStatus = "hold" | "confirmed" | "cancelled";
type StatusFilter = BookingStatus | "all";

type AdminBooking = {
  id: string;
  reference?: string;
  apartmentSlug: string;
  apartmentTitle: string;
  start: string;
  end: string;
  adults: number;
  children: number;
  guests: number;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  note: string;
  status: BookingStatus;
  holdExpiresAt?: string;
  nightlySubtotal: number | null;
  cleaningFee: number | null;
  total: number | null;
  marketingOptIn: boolean;
  createdAt?: string;
  accountId: string | null;
  accountName: string | null;
  accountEmail: string | null;
};

type BlockedRange = {
  id: string;
  apartmentSlug: string;
  apartmentTitle: string;
  start: string;
  end: string;
  reason: string;
  createdAt?: string;
};

type MarketingContact = {
  email: string;
  name: string;
  phone: string;
  source: string;
  optInAt: string;
  unsubscribedAt?: string | null;
  active: boolean;
  updatedAt: string;
};

type EmailEvent = {
  id: string;
  campaignId: string | null;
  bookingId: string | null;
  kind: string;
  recipientEmail: string;
  recipientName: string;
  subject: string;
  status: "sent" | "skipped" | "failed";
  providerMessageId: string;
  errorMessage: string;
  metadata?: {
    apartment?: string;
    status?: string;
    holdExpiresAt?: string | null;
    actionPath?: string;
    discountCode?: string;
    inquiryId?: string;
    guestEmail?: string;
    unsubscribeUrl?: string;
    [key: string]: unknown;
  };
  createdAt: string;
  sentAt?: string;
};

type DiscountCampaign = {
  id: string;
  title: string;
  discountCode: string;
  subject: string;
  message: string;
  audienceCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  createdAt: string;
};

type EmailDelivery = {
  configured: boolean;
  mode: "smtp" | "logging-only";
  host: string;
  port: string;
  from: string;
  adminEmail: string;
  missing: string[];
};

type ContactInquiry = {
  id: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  marketingOptIn: boolean;
  createdAt: string;
};

type ChatConversation = {
  id: string;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  status: "open" | "closed";
  preview: string;
  unreadCount: number;
  lastMessageAt: string;
  lastGuestMessageAt?: string | null;
  lastAdminMessageAt?: string | null;
  lastAdminReadAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

type ChatMessage = {
  id: string;
  conversationId: string;
  sender: "guest" | "admin" | "system";
  body: string;
  createdAt: string;
};

type PushNotifications = {
  configured: boolean;
  publicKey: string;
  subject: string;
  subscriptionCount: number;
  missing: string[];
};

type ChatBusinessHoursDay = {
  enabled: boolean;
  start: string;
  end: string;
};

type ChatBusinessHours = Record<(typeof chatWeekdays)[number]["id"], ChatBusinessHoursDay>;

type ChatSettings = {
  responseTimeLabel: string;
  onlineMessage: string;
  offlineMessage: string;
  handoffPrompt: string;
  businessTimezone: string;
  businessHours: ChatBusinessHours;
  requireEmail: boolean;
  isBusinessHours: boolean;
  businessHoursSummary: string;
  updatedAt?: string;
};

type ChatFaq = {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  enabled: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
};

type AdminOverview = {
  stats: {
    hold: number;
    confirmed: number;
    cancelled: number;
    marketingContacts: number;
    blockedRanges: number;
    emailEvents: number;
    contactInquiries: number;
    discountCampaigns: number;
    openChats: number;
    closedChats: number;
    unreadChatMessages: number;
    pushSubscriptions: number;
  };
  bookings: AdminBooking[];
  blockedRanges: BlockedRange[];
  marketingContacts: MarketingContact[];
  emailEvents: EmailEvent[];
  contactInquiries: ContactInquiry[];
  discountCampaigns: DiscountCampaign[];
  emailDelivery: EmailDelivery;
  chatConversations: ChatConversation[];
  chatSettings: ChatSettings;
  chatFaqs: ChatFaq[];
  pushNotifications: PushNotifications;
};

function storedToken() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.localStorage.getItem(adminStorageKey) ?? "";
}

function saveToken(token: string) {
  if (typeof window === "undefined") {
    return;
  }

  if (token) {
    window.localStorage.setItem(adminStorageKey, token);
    return;
  }

  window.localStorage.removeItem(adminStorageKey);
}

function todayDate() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: bookingTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function addDays(dateOnly: string, days: number) {
  const date = new Date(`${dateOnly}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function money(value: number | null | undefined) {
  if (typeof value !== "number") {
    return "Quote pending";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateOnly: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${dateOnly}T00:00:00.000Z`));
}

function formatDateTime(value?: string) {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

async function readJson(response: Response) {
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.error?.message ?? "Request failed");
  }

  return data;
}

async function adminRequest(token: string, path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);

  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return fetch(path, { ...init, headers });
}

function statusText(booking: AdminBooking) {
  if (booking.status === "hold" && booking.holdExpiresAt) {
    return `Hold expires ${formatDateTime(booking.holdExpiresAt)}`;
  }

  return booking.status;
}

function bookingReference(booking: AdminBooking) {
  return booking.reference || booking.id.slice(0, 8).toUpperCase();
}

function bookingStatusAction(status: BookingStatus) {
  if (status === "confirmed") {
    return {
      title: "Confirm this stay?",
      body: "The dates will remain reserved and the guest will receive a confirmation email.",
      button: "Yes, confirm stay",
    };
  }

  if (status === "cancelled") {
    return {
      title: "Cancel this booking?",
      body: "The dates will be released and the guest will receive a cancellation email.",
      button: "Yes, cancel booking",
    };
  }

  return {
    title: "Restore this hold?",
    body: "The request will reopen with a fresh temporary hold and the guest will be notified.",
    button: "Yes, restore hold",
  };
}

function emailEventDetail(event: EmailEvent) {
  const details = [event.kind];

  if (event.metadata?.discountCode) {
    details.push(`Code ${event.metadata.discountCode}`);
  }

  if (event.metadata?.inquiryId) {
    details.push(`Inquiry ${event.metadata.inquiryId.slice(0, 8).toUpperCase()}`);
  }

  if (event.metadata?.guestEmail) {
    details.push(event.metadata.guestEmail);
  }

  if (event.metadata?.actionPath) {
    details.push(`CTA ${event.metadata.actionPath}`);
  }

  if (event.metadata?.holdExpiresAt) {
    details.push(`Hold ${formatDateTime(event.metadata.holdExpiresAt)}`);
  }

  details.push(formatDateTime(event.createdAt));

  return details.filter(Boolean).join(" · ");
}

function chatVisitorLabel(conversation: ChatConversation) {
  return conversation.visitorName || conversation.visitorEmail || conversation.visitorPhone || "Website guest";
}

function chatVisitorContact(conversation: ChatConversation) {
  return [conversation.visitorEmail, conversation.visitorPhone].filter(Boolean).join(" · ");
}

function mergeChatConversation(conversations: ChatConversation[], conversation: ChatConversation) {
  const byId = new Map(conversations.map((item) => [item.id, item]));
  byId.set(conversation.id, conversation);

  return Array.from(byId.values()).sort((left, right) => {
    if (left.status !== right.status) {
      return left.status === "open" ? -1 : 1;
    }

    return new Date(right.lastMessageAt).valueOf() - new Date(left.lastMessageAt).valueOf();
  });
}

function mergeChatMessages(messages: ChatMessage[], incoming: ChatMessage[]) {
  const byId = new Map(messages.map((item) => [item.id, item]));

  for (const message of incoming) {
    byId.set(message.id, message);
  }

  return Array.from(byId.values()).sort((left, right) => new Date(left.createdAt).valueOf() - new Date(right.createdAt).valueOf());
}

function overviewWithChatConversation(overview: AdminOverview, conversation: ChatConversation): AdminOverview {
  const previous = overview.chatConversations.find((chat) => chat.id === conversation.id);
  const chatConversations = mergeChatConversation(overview.chatConversations, conversation);
  let openChats = overview.stats.openChats;
  let closedChats = overview.stats.closedChats;

  if (!previous) {
    if (conversation.status === "open") {
      openChats += 1;
    } else {
      closedChats += 1;
    }
  } else if (previous.status !== conversation.status) {
    if (previous.status === "open") {
      openChats = Math.max(0, openChats - 1);
      closedChats += 1;
    } else {
      closedChats = Math.max(0, closedChats - 1);
      openChats += 1;
    }
  }

  return {
    ...overview,
    stats: {
      ...overview.stats,
      openChats,
      closedChats,
      unreadChatMessages: Math.max(0, overview.stats.unreadChatMessages - (previous?.unreadCount ?? 0) + conversation.unreadCount),
    },
    chatConversations,
  };
}

function parseAdminChatEvent(event: Event) {
  try {
    return JSON.parse((event as MessageEvent).data) as {
      conversation?: ChatConversation;
      message?: ChatMessage;
      messages?: ChatMessage[];
    };
  } catch {
    return null;
  }
}

type ChatMessagesResponse = {
  conversation: ChatConversation;
  message?: ChatMessage;
  messages: ChatMessage[];
};

type ChatConversationResponse = {
  conversation: ChatConversation;
};

function pushSupported() {
  return typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
}

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);

  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }

  return output;
}

export function AdminPage() {
  const location = useLocation();
  const chatSettingsOnly = location.pathname.endsWith("/chat-settings");
  const bookableApartments = apartments.filter((apartment) => apartment.status === "available");
  const [token, setToken] = useState(storedToken);
  const [tokenInput, setTokenInput] = useState(storedToken);
  const [authorized, setAuthorized] = useState(Boolean(storedToken()));
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState("");
  const [pendingStatusAction, setPendingStatusAction] = useState<{ bookingId: string; status: BookingStatus } | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [blockApartment, setBlockApartment] = useState(bookableApartments[0]?.slug ?? "");
  const [blockStart, setBlockStart] = useState(todayDate());
  const [blockEnd, setBlockEnd] = useState(addDays(todayDate(), 1));
  const [blockReason, setBlockReason] = useState("");
  const [campaignTitle, setCampaignTitle] = useState("Seed House guest discount");
  const [campaignCode, setCampaignCode] = useState("SEEDHOUSE10");
  const [campaignSubject, setCampaignSubject] = useState("A Deposit Seed House discount for your next stay");
  const [campaignMessage, setCampaignMessage] = useState(
    "Thank you for staying connected with The Deposit Seed House. Use this discount code when you contact us about your next Deposit stay.",
  );
  const [testEmail, setTestEmail] = useState("");
  const [testName, setTestName] = useState("");
  const [selectedChatId, setSelectedChatId] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatReply, setChatReply] = useState("");
  const [chatSettingsDraft, setChatSettingsDraft] = useState<ChatSettings | null>(null);
  const [pushStatus, setPushStatus] = useState("");
  const [pushAction, setPushAction] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "default",
  );

  const marketingExport = useMemo(() => {
    return (overview?.marketingContacts ?? [])
      .map((contact) => [contact.email, contact.name, contact.phone].filter(Boolean).join(", "))
      .join("\n");
  }, [overview?.marketingContacts]);
  const selectedChat = useMemo(() => {
    return overview?.chatConversations.find((conversation) => conversation.id === selectedChatId) ?? overview?.chatConversations[0] ?? null;
  }, [overview?.chatConversations, selectedChatId]);

  const loadOverview = async (nextToken = token, nextStatus = statusFilter) => {
    if (!nextToken) {
      setAuthorized(false);
      setOverview(null);
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const params = new URLSearchParams({
        status: nextStatus,
        limit: "180",
      });
      const response = await adminRequest(nextToken, `/api/admin/overview?${params.toString()}`);
      const data = (await readJson(response)) as AdminOverview;

      setOverview(data);
      setAuthorized(true);
      setToken(nextToken);
      saveToken(nextToken);
    } catch (error) {
      setAuthorized(false);
      setOverview(null);
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to load admin dashboard." });
    } finally {
      setLoading(false);
    }
  };

  const loadChatMessages = async (conversationId: string) => {
    if (!token || !conversationId) {
      return;
    }

    setActionId(`chat:${conversationId}:load`);
    setMessage(null);

    try {
      const data = (await adminRequest(token, `/api/admin/chat/conversations/${conversationId}/messages`).then(readJson)) as ChatMessagesResponse;
      setSelectedChatId(data.conversation.id);
      setChatMessages(data.messages ?? []);
      setOverview((current) => (current ? overviewWithChatConversation(current, data.conversation) : current));
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to load chat." });
    } finally {
      setActionId("");
    }
  };

  const sendChatReply = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedChat || !chatReply.trim()) {
      return;
    }

    setActionId(`chat:${selectedChat.id}:reply`);
    setMessage(null);

    try {
      const data = (await adminRequest(token, `/api/admin/chat/conversations/${selectedChat.id}/messages`, {
        method: "POST",
        body: JSON.stringify({ message: chatReply }),
      }).then(readJson)) as ChatMessagesResponse;

      setChatReply("");
      setChatMessages(data.messages ?? (data.message ? mergeChatMessages(chatMessages, [data.message]) : chatMessages));
      setOverview((current) => (current ? overviewWithChatConversation(current, data.conversation) : current));
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to send chat reply." });
    } finally {
      setActionId("");
    }
  };

  const updateChatStatus = async (conversation: ChatConversation, status: ChatConversation["status"]) => {
    setActionId(`chat:${conversation.id}:${status}`);
    setMessage(null);

    try {
      const data = (await adminRequest(token, `/api/admin/chat/conversations/${conversation.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }).then(readJson)) as ChatConversationResponse;

      setOverview((current) => (current ? overviewWithChatConversation(current, data.conversation) : current));
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to update chat." });
    } finally {
      setActionId("");
    }
  };

  const enablePushNotifications = async () => {
    setPushStatus("");
    setMessage(null);
    setPushAction(true);

    try {
      if (!pushSupported()) {
        throw new Error("This browser does not support push notifications.");
      }

      const configData = await adminRequest(token, "/api/admin/push/config").then(readJson);
      const publicKey = configData.pushNotifications?.publicKey || overview?.pushNotifications.publicKey || "";

      if (!publicKey) {
        throw new Error("WEB_PUSH_PUBLIC_KEY is not configured.");
      }

      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission !== "granted") {
        throw new Error("Browser notifications were not allowed.");
      }

      const registration = await navigator.serviceWorker.register("/chat-push-sw.js");
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));
      const data = await adminRequest(token, "/api/admin/push/subscriptions", {
        method: "POST",
        body: JSON.stringify({ subscription }),
      }).then(readJson);

      setPushStatus("Push notifications are enabled for this browser.");
      setOverview((current) => (current ? { ...current, pushNotifications: data.pushNotifications, stats: { ...current.stats, pushSubscriptions: data.pushNotifications.subscriptionCount } } : current));
    } catch (error) {
      setPushStatus(error instanceof Error ? error.message : "Unable to enable push notifications.");
    } finally {
      setPushAction(false);
    }
  };

  useEffect(() => {
    if (token && !overview) {
      void loadOverview(token, statusFilter);
    }
  }, []);

  useEffect(() => {
    if (!authorized || !token) {
      return undefined;
    }

    const stream = new EventSource(`/api/admin/chat/events?token=${encodeURIComponent(token)}`);

    stream.addEventListener("chat-message", (event) => {
      const data = parseAdminChatEvent(event);

      if (!data) {
        return;
      }

      const conversation: ChatConversation | null = data.conversation ?? null;
      const message: ChatMessage | null = data.message ?? null;
      const messages = data.messages ?? [];

      if (conversation) {
        setOverview((current) => (current ? overviewWithChatConversation(current, conversation) : current));
      }

      if (message && conversation?.id === selectedChatId) {
        setChatMessages((current) => mergeChatMessages(current, [message]));
      }

      if (messages.length > 0 && conversation?.id === selectedChatId) {
        setChatMessages((current) => mergeChatMessages(current, messages));
      }

      if (message?.sender === "guest" && conversation?.id !== selectedChatId) {
        const title = conversation ? `New chat from ${chatVisitorLabel(conversation)}` : "New website chat";
        setMessage({ tone: "success", text: title });

        if (notificationPermission === "granted") {
          new Notification(title, {
            body: message.body,
            icon: "/assets/brand/apple-touch-icon.png",
          });
        }
      }
    });

    stream.addEventListener("chat-read", (event) => {
      const data = parseAdminChatEvent(event);
      const conversation: ChatConversation | null = data?.conversation ?? null;

      if (conversation) {
        setOverview((current) => (current ? overviewWithChatConversation(current, conversation) : current));
      }
    });

    stream.addEventListener("chat-status", (event) => {
      const data = parseAdminChatEvent(event);
      const conversation: ChatConversation | null = data?.conversation ?? null;

      if (conversation) {
        setOverview((current) => (current ? overviewWithChatConversation(current, conversation) : current));
      }
    });

    return () => {
      stream.close();
    };
  }, [authorized, token, selectedChatId, notificationPermission]);

  useEffect(() => {
    if (!selectedChatId && overview?.chatConversations[0]) {
      setSelectedChatId(overview.chatConversations[0].id);
    }
  }, [overview?.chatConversations, selectedChatId]);

  useEffect(() => {
    if (selectedChatId && authorized && token) {
      void loadChatMessages(selectedChatId);
    }
  }, [selectedChatId]);

  useEffect(() => {
    if (overview?.chatSettings) {
      setChatSettingsDraft(JSON.parse(JSON.stringify(overview.chatSettings)) as ChatSettings);
    }
  }, [overview?.chatSettings?.updatedAt]);

  const onTokenSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await loadOverview(tokenInput.trim(), statusFilter);
  };

  const clearAdminSession = () => {
    saveToken("");
    setToken("");
    setTokenInput("");
    setAuthorized(false);
    setOverview(null);
    setMessage(null);
  };

  const updateBookingStatus = async (booking: AdminBooking, status: BookingStatus) => {
    setActionId(`${booking.id}:${status}`);
    setMessage(null);

    try {
      await adminRequest(token, `/api/admin/bookings/${booking.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }).then(readJson);
      setMessage({ tone: "success", text: `${booking.apartmentTitle} booking marked ${status}.` });
      await loadOverview(token, statusFilter);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to update booking." });
    } finally {
      setActionId("");
      setPendingStatusAction(null);
    }
  };

  const createBlockedRange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActionId("block:create");
    setMessage(null);

    try {
      await adminRequest(token, "/api/admin/blocked-ranges", {
        method: "POST",
        body: JSON.stringify({
          apartment: blockApartment,
          start: blockStart,
          end: blockEnd,
          reason: blockReason,
        }),
      }).then(readJson);
      setMessage({ tone: "success", text: "Dates blocked." });
      setBlockReason("");
      await loadOverview(token, statusFilter);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to block dates." });
    } finally {
      setActionId("");
    }
  };

  const removeBlockedRange = async (range: BlockedRange) => {
    setActionId(`block:${range.id}`);
    setMessage(null);

    try {
      const response = await adminRequest(token, `/api/admin/blocked-ranges/${range.id}`, { method: "DELETE" });

      if (!response.ok) {
        await readJson(response);
      }

      setMessage({ tone: "success", text: "Blocked dates removed." });
      await loadOverview(token, statusFilter);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to remove blocked dates." });
    } finally {
      setActionId("");
    }
  };

  const sendDiscountCampaign = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActionId("campaign:send");
    setMessage(null);

    try {
      const data = await adminRequest(token, "/api/admin/campaigns/discount", {
        method: "POST",
        body: JSON.stringify({
          title: campaignTitle,
          discountCode: campaignCode,
          subject: campaignSubject,
          message: campaignMessage,
        }),
      }).then(readJson);
      const campaign = data.campaign as DiscountCampaign;

      setMessage({
        tone: "success",
        text: `Campaign queued for ${campaign.audienceCount} contact${campaign.audienceCount === 1 ? "" : "s"}: ${campaign.sentCount} sent, ${campaign.skippedCount} skipped, ${campaign.failedCount} failed.`,
      });
      await loadOverview(token, statusFilter);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to send discount campaign." });
    } finally {
      setActionId("");
    }
  };

  const sendTestEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActionId("email:test");
    setMessage(null);

    try {
      const data = await adminRequest(token, "/api/admin/email/test", {
        method: "POST",
        body: JSON.stringify({
          to: testEmail,
          name: testName,
        }),
      }).then(readJson);
      const status = data.event?.status ?? "unknown";

      setMessage({
        tone: status === "failed" ? "error" : "success",
        text: status === "sent" ? "Test email sent." : status === "skipped" ? "Test email logged as skipped because SMTP is not configured." : data.event?.errorMessage ?? "Test email failed.",
      });
      await loadOverview(token, statusFilter);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to send test email." });
    } finally {
      setActionId("");
    }
  };

  const onStatusFilterChange = async (nextStatus: StatusFilter) => {
    setStatusFilter(nextStatus);
    await loadOverview(token, nextStatus);
  };

  const updateChatSettingsDraft = (patch: Partial<ChatSettings>) => {
    setChatSettingsDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const updateChatBusinessHours = (day: keyof ChatBusinessHours, patch: Partial<ChatBusinessHoursDay>) => {
    setChatSettingsDraft((current) =>
      current
        ? {
            ...current,
            businessHours: {
              ...current.businessHours,
              [day]: {
                ...current.businessHours[day],
                ...patch,
              },
            },
          }
        : current,
    );
  };

  const saveChatSettings = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!chatSettingsDraft) {
      return;
    }

    setActionId("chat-settings:save");
    setMessage(null);

    try {
      const data = await adminRequest(token, "/api/admin/chat/settings", {
        method: "PATCH",
        body: JSON.stringify(chatSettingsDraft),
      }).then(readJson);

      setOverview((current) => (current ? { ...current, chatSettings: data.chatSettings as ChatSettings } : current));
      setMessage({ tone: "success", text: "Chat settings saved." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to save chat settings." });
    } finally {
      setActionId("");
    }
  };

  const createChatFaq = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActionId("chat-faq:create");
    setMessage(null);

    try {
      const form = event.currentTarget;
      const formData = new FormData(form);
      const data = await adminRequest(token, "/api/admin/chat/faqs", {
        method: "POST",
        body: JSON.stringify({
          question: formData.get("question"),
          answer: formData.get("answer"),
          keywords: formData.get("keywords"),
          enabled: formData.get("enabled") === "on",
          sortOrder: Number(formData.get("sortOrder") ?? 100),
        }),
      }).then(readJson);

      form.reset();
      setOverview((current) => (current ? { ...current, chatFaqs: data.chatFaqs as ChatFaq[] } : current));
      setMessage({ tone: "success", text: "Chat auto-answer added." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to add auto-answer." });
    } finally {
      setActionId("");
    }
  };

  const saveChatFaq = async (event: FormEvent<HTMLFormElement>, faq: ChatFaq) => {
    event.preventDefault();
    setActionId(`chat-faq:${faq.id}:save`);
    setMessage(null);

    try {
      const formData = new FormData(event.currentTarget);
      const data = await adminRequest(token, `/api/admin/chat/faqs/${faq.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          question: formData.get("question"),
          answer: formData.get("answer"),
          keywords: formData.get("keywords"),
          enabled: formData.get("enabled") === "on",
          sortOrder: Number(formData.get("sortOrder") ?? faq.sortOrder),
        }),
      }).then(readJson);

      setOverview((current) => (current ? { ...current, chatFaqs: data.chatFaqs as ChatFaq[] } : current));
      setMessage({ tone: "success", text: "Chat auto-answer saved." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to save auto-answer." });
    } finally {
      setActionId("");
    }
  };

  const deleteChatFaq = async (faq: ChatFaq) => {
    setActionId(`chat-faq:${faq.id}:delete`);
    setMessage(null);

    try {
      const data = await adminRequest(token, `/api/admin/chat/faqs/${faq.id}`, { method: "DELETE" }).then(readJson);

      setOverview((current) => (current ? { ...current, chatFaqs: data.chatFaqs as ChatFaq[] } : current));
      setMessage({ tone: "success", text: "Chat auto-answer removed." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to remove auto-answer." });
    } finally {
      setActionId("");
    }
  };

  return (
    <>
      <SEO
        title={chatSettingsOnly ? "Chat Settings | The Deposit Seed House" : "Owner Dashboard | The Deposit Seed House"}
        description={chatSettingsOnly ? "Owner chat automation settings for The Deposit Seed House." : "Owner booking dashboard for The Deposit Seed House."}
        image={siteInfo.exteriorImage}
        canonicalPath={chatSettingsOnly ? "/admin/chat-settings" : "/admin"}
        noIndex
      />
      <section className="page-hero page-hero--admin">
        <div>
          <p className="eyebrow">Owner dashboard</p>
          <h1>{chatSettingsOnly ? "Chat settings." : "Booking operations."}</h1>
          <p>{chatSettingsOnly ? "Control business hours, response promises, and automatic answers guests see before reaching the owner." : "Review holds, confirm stays, block dates, and export opted-in guests."}</p>
        </div>
      </section>
      <section className="section admin-section">
        {message && (
          <div className={`admin-message admin-message--${message.tone}`} role="status" aria-live="polite">
            {message.tone === "success" ? <Check size={18} aria-hidden="true" /> : <X size={18} aria-hidden="true" />}
            <span>{message.text}</span>
          </div>
        )}

        {!authorized && (
          <form className="admin-login-card" onSubmit={onTokenSubmit}>
            <LockKeyhole size={28} aria-hidden="true" />
            <div>
              <p className="eyebrow">Secure access</p>
              <h2>Enter owner passcode</h2>
              <p>Use the configured admin token for this deployment.</p>
            </div>
            <label>
              <span>Passcode</span>
              <input
                type="password"
                autoComplete="current-password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                required
              />
            </label>
            <button className="button button--primary button--full" type="submit" disabled={loading}>
              <ShieldCheck size={18} aria-hidden="true" />
              {loading ? "Checking" : "Open dashboard"}
            </button>
          </form>
        )}

        {authorized && !overview && (
          <div className="admin-login-card">
            <RefreshCcw size={28} aria-hidden="true" />
            <div>
              <p className="eyebrow">Loading</p>
              <h2>Opening dashboard</h2>
              <p>Checking the saved owner passcode.</p>
            </div>
          </div>
        )}

        {authorized && overview && (
          <div className="admin-dashboard">
            <div className="admin-toolbar">
              <div>
                <p className="eyebrow">{chatSettingsOnly ? "Chat automation" : "Live dashboard"}</p>
                <h2>{chatSettingsOnly ? "Support controls" : "Owner controls"}</h2>
              </div>
              <div className="admin-toolbar__actions">
                <button className="icon-button" type="button" onClick={() => loadOverview(token, statusFilter)} aria-label="Refresh dashboard" disabled={loading}>
                  <RefreshCcw size={20} aria-hidden="true" />
                </button>
                <button className="button button--light" type="button" onClick={clearAdminSession}>
                  <LogOut size={18} aria-hidden="true" />
                  Sign out
                </button>
              </div>
            </div>

            <nav className="admin-ops-nav" aria-label="Owner dashboard sections">
              {adminNavItems.map((item) => (
                <a key={item.href} href={item.href}>
                  {item.label}
                </a>
              ))}
            </nav>

            {chatSettingsDraft && (
              <section className="admin-panel admin-chat-settings-panel" id="admin-chat-settings" aria-labelledby="admin-chat-settings-title">
                <div className="admin-panel__heading">
                  <div>
                    <p className="eyebrow">Automation</p>
                    <h2 id="admin-chat-settings-title">Chat settings and auto-answers</h2>
                  </div>
                  <Link className="button button--light" to={chatSettingsOnly ? "/admin" : "/admin/chat-settings"}>
                    {chatSettingsOnly ? "Back to dashboard" : "Open settings page"}
                  </Link>
                </div>

                <form className="admin-chat-settings-form" onSubmit={saveChatSettings}>
                  <label>
                    <span>Response promise</span>
                    <input
                      maxLength={220}
                      value={chatSettingsDraft.responseTimeLabel}
                      onChange={(event) => updateChatSettingsDraft({ responseTimeLabel: event.target.value })}
                      required
                    />
                  </label>
                  <label>
                    <span>Timezone</span>
                    <input
                      maxLength={80}
                      value={chatSettingsDraft.businessTimezone}
                      onChange={(event) => updateChatSettingsDraft({ businessTimezone: event.target.value })}
                      required
                    />
                  </label>
                  <label className="admin-chat-settings-form__wide">
                    <span>Business-hours message</span>
                    <textarea
                      rows={3}
                      maxLength={420}
                      value={chatSettingsDraft.onlineMessage}
                      onChange={(event) => updateChatSettingsDraft({ onlineMessage: event.target.value })}
                      required
                    />
                  </label>
                  <label className="admin-chat-settings-form__wide">
                    <span>After-hours message</span>
                    <textarea
                      rows={3}
                      maxLength={420}
                      value={chatSettingsDraft.offlineMessage}
                      onChange={(event) => updateChatSettingsDraft({ offlineMessage: event.target.value })}
                      required
                    />
                  </label>
                  <label className="admin-chat-settings-form__wide">
                    <span>Pre-chat prompt</span>
                    <textarea
                      rows={3}
                      maxLength={360}
                      value={chatSettingsDraft.handoffPrompt}
                      onChange={(event) => updateChatSettingsDraft({ handoffPrompt: event.target.value })}
                      required
                    />
                  </label>
                  <label className="admin-chat-toggle admin-chat-settings-form__wide">
                    <input type="checkbox" checked={chatSettingsDraft.requireEmail} onChange={(event) => updateChatSettingsDraft({ requireEmail: event.target.checked })} />
                    <span>Require email before starting chat for signed-out visitors</span>
                  </label>

                  <div className="admin-chat-settings-form__wide">
                    <div className="admin-subheading-row">
                      <h3 className="admin-subheading">Business hours</h3>
                      <span>{overview.chatSettings.businessHoursSummary || "No active hours configured"}</span>
                    </div>
                    <div className="admin-chat-hours-grid">
                      {chatWeekdays.map((day) => {
                        const value = chatSettingsDraft.businessHours[day.id];

                        return (
                          <div className="admin-chat-hours-row" key={day.id}>
                            <label className="admin-chat-hours-row__enabled">
                              <input type="checkbox" checked={value.enabled} onChange={(event) => updateChatBusinessHours(day.id, { enabled: event.target.checked })} />
                              <span>{day.label}</span>
                            </label>
                            <label>
                              <span>Start</span>
                              <input type="time" value={value.start} onChange={(event) => updateChatBusinessHours(day.id, { start: event.target.value })} disabled={!value.enabled} required />
                            </label>
                            <label>
                              <span>End</span>
                              <input type="time" value={value.end} onChange={(event) => updateChatBusinessHours(day.id, { end: event.target.value })} disabled={!value.enabled} required />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button className="button button--primary admin-chat-settings-form__wide" type="submit" disabled={actionId === "chat-settings:save"}>
                    <Check size={18} aria-hidden="true" />
                    {actionId === "chat-settings:save" ? "Saving settings" : "Save chat settings"}
                  </button>
                </form>

                <div className="admin-chat-faq-layout">
                  <form className="admin-chat-faq-create" onSubmit={createChatFaq}>
                    <div>
                      <p className="eyebrow">New auto-answer</p>
                      <h3 className="admin-subheading">Add a smart reply</h3>
                    </div>
                    <label>
                      <span>Question</span>
                      <input name="question" maxLength={220} placeholder="When can I check out?" required />
                    </label>
                    <label>
                      <span>Answer</span>
                      <textarea name="answer" rows={5} maxLength={1400} placeholder="Check-out is at 11:00 AM..." required />
                    </label>
                    <label>
                      <span>Keywords</span>
                      <input name="keywords" maxLength={520} placeholder="checkout, check out, late checkout" />
                    </label>
                    <div className="admin-chat-faq-create__footer">
                      <label>
                        <span>Sort</span>
                        <input name="sortOrder" type="number" min={0} max={10000} defaultValue={100} />
                      </label>
                      <label className="admin-chat-toggle">
                        <input name="enabled" type="checkbox" defaultChecked />
                        <span>Enabled</span>
                      </label>
                    </div>
                    <button className="button button--secondary button--full" type="submit" disabled={actionId === "chat-faq:create"}>
                      <MessageCircle size={18} aria-hidden="true" />
                      {actionId === "chat-faq:create" ? "Adding answer" : "Add auto-answer"}
                    </button>
                  </form>

                  <div className="admin-chat-faq-list">
                    {overview.chatFaqs.map((faq) => (
                      <form className="admin-chat-faq-card" key={faq.id} onSubmit={(event) => saveChatFaq(event, faq)}>
                        <div className="admin-chat-faq-card__top">
                          <strong>{faq.question}</strong>
                          <span className={`admin-email-status admin-email-status--${faq.enabled ? "sent" : "skipped"}`}>{faq.enabled ? "Active" : "Hidden"}</span>
                        </div>
                        <label>
                          <span>Question</span>
                          <input name="question" maxLength={220} defaultValue={faq.question} required />
                        </label>
                        <label>
                          <span>Answer</span>
                          <textarea name="answer" rows={4} maxLength={1400} defaultValue={faq.answer} required />
                        </label>
                        <div className="admin-chat-faq-card__meta">
                          <label>
                            <span>Keywords</span>
                            <input name="keywords" maxLength={520} defaultValue={faq.keywords.join(", ")} />
                          </label>
                          <label>
                            <span>Sort</span>
                            <input name="sortOrder" type="number" min={0} max={10000} defaultValue={faq.sortOrder} />
                          </label>
                          <label className="admin-chat-toggle">
                            <input name="enabled" type="checkbox" defaultChecked={faq.enabled} />
                            <span>Enabled</span>
                          </label>
                        </div>
                        <div className="admin-chat-faq-card__actions">
                          <button className="button button--primary" type="submit" disabled={actionId === `chat-faq:${faq.id}:save`}>
                            <Check size={17} aria-hidden="true" />
                            {actionId === `chat-faq:${faq.id}:save` ? "Saving" : "Save"}
                          </button>
                          <button className="button button--light" type="button" onClick={() => deleteChatFaq(faq)} disabled={actionId === `chat-faq:${faq.id}:delete`}>
                            <X size={17} aria-hidden="true" />
                            {actionId === `chat-faq:${faq.id}:delete` ? "Deleting" : "Delete"}
                          </button>
                        </div>
                      </form>
                    ))}
                    {overview.chatFaqs.length === 0 && <div className="admin-empty-state">No chat auto-answers yet.</div>}
                  </div>
                </div>
              </section>
            )}

            <div className="admin-stat-grid">
              <div>
                <Clock size={20} aria-hidden="true" />
                <span>Holds</span>
                <strong>{overview.stats.hold}</strong>
              </div>
              <div>
                <CalendarCheck size={20} aria-hidden="true" />
                <span>Confirmed</span>
                <strong>{overview.stats.confirmed}</strong>
              </div>
              <div>
                <Ban size={20} aria-hidden="true" />
                <span>Cancelled</span>
                <strong>{overview.stats.cancelled}</strong>
              </div>
              <div>
                <CalendarOff size={20} aria-hidden="true" />
                <span>Blocked</span>
                <strong>{overview.stats.blockedRanges}</strong>
              </div>
              <div>
                <Mail size={20} aria-hidden="true" />
                <span>Opt-ins</span>
                <strong>{overview.stats.marketingContacts}</strong>
              </div>
              <div>
                <Send size={20} aria-hidden="true" />
                <span>Emails</span>
                <strong>{overview.stats.emailEvents}</strong>
              </div>
              <div>
                <Inbox size={20} aria-hidden="true" />
                <span>Inquiries</span>
                <strong>{overview.stats.contactInquiries}</strong>
              </div>
              <div>
                <Tag size={20} aria-hidden="true" />
                <span>Campaigns</span>
                <strong>{overview.stats.discountCampaigns}</strong>
              </div>
              <div>
                <MessageCircle size={20} aria-hidden="true" />
                <span>Unread chat</span>
                <strong>{overview.stats.unreadChatMessages}</strong>
              </div>
              <div>
                <Bell size={20} aria-hidden="true" />
                <span>Push devices</span>
                <strong>{overview.stats.pushSubscriptions}</strong>
              </div>
            </div>

            <section className="admin-panel admin-chat-panel" id="admin-chat" aria-labelledby="admin-chat-title">
              <div className="admin-panel__heading">
                <div>
                  <p className="eyebrow">Guest messaging</p>
                  <h2 id="admin-chat-title">Live chat inbox</h2>
                </div>
                <div className="admin-chat-panel__actions">
                  <span className={`admin-email-status admin-email-status--${overview.pushNotifications.configured ? "sent" : "skipped"}`}>
                    {overview.pushNotifications.configured ? "Push ready" : "Push setup needed"}
                  </span>
                  <button
                    className="button button--secondary"
                    type="button"
                    onClick={enablePushNotifications}
                    disabled={pushAction || !overview.pushNotifications.configured || !pushSupported()}
                  >
                    <Bell size={18} aria-hidden="true" />
                    {pushAction ? "Enabling" : "Enable push"}
                  </button>
                </div>
              </div>
              {overview.pushNotifications.missing.length > 0 && (
                <div className="admin-delivery-warning">
                  <strong>Missing push settings:</strong>
                  <span>{overview.pushNotifications.missing.join(", ")}</span>
                </div>
              )}
              {pushStatus && <div className="admin-chat-status">{pushStatus}</div>}
              <div className="admin-chat-layout">
                <div className="admin-chat-list" aria-label="Chat conversations">
                  {overview.chatConversations.map((conversation) => (
                    <button
                      className={`admin-chat-list__item ${selectedChat?.id === conversation.id ? "admin-chat-list__item--active" : ""}`}
                      type="button"
                      key={conversation.id}
                      onClick={() => setSelectedChatId(conversation.id)}
                    >
                      <span>
                        <strong>{chatVisitorLabel(conversation)}</strong>
                        <small>{formatDateTime(conversation.lastMessageAt)}</small>
                      </span>
                      <em>{conversation.preview || "No messages yet"}</em>
                      <b>{conversation.unreadCount > 0 ? conversation.unreadCount : conversation.status}</b>
                    </button>
                  ))}
                  {overview.chatConversations.length === 0 && <div className="admin-empty-state">No guest chats yet.</div>}
                </div>
                <div className="admin-chat-thread">
                  {selectedChat ? (
                    <>
                      <div className="admin-chat-thread__header">
                        <div>
                          <h3>{chatVisitorLabel(selectedChat)}</h3>
                          <p>{chatVisitorContact(selectedChat) || "Website guest"}</p>
                        </div>
                        <button
                          className="button button--light"
                          type="button"
                          onClick={() => updateChatStatus(selectedChat, selectedChat.status === "open" ? "closed" : "open")}
                          disabled={Boolean(actionId)}
                        >
                          {selectedChat.status === "open" ? "Close chat" : "Reopen chat"}
                        </button>
                      </div>
                      <div className="admin-chat-messages">
                        {chatMessages.map((chatMessage) => (
                          <div className={`admin-chat-bubble admin-chat-bubble--${chatMessage.sender}`} key={chatMessage.id}>
                            <span>{chatMessage.sender === "admin" ? "You" : chatVisitorLabel(selectedChat)}</span>
                            <p>{chatMessage.body}</p>
                          </div>
                        ))}
                        {chatMessages.length === 0 && <div className="admin-empty-state">Open a conversation to load messages.</div>}
                      </div>
                      <form className="admin-chat-compose" onSubmit={sendChatReply}>
                        <label>
                          <span>Reply</span>
                          <textarea rows={4} maxLength={1800} value={chatReply} onChange={(event) => setChatReply(event.target.value)} required />
                        </label>
                        <button className="button button--primary" type="submit" disabled={Boolean(actionId) || !chatReply.trim()}>
                          <Send size={18} aria-hidden="true" />
                          {actionId === `chat:${selectedChat.id}:reply` ? "Sending" : "Send reply"}
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="admin-empty-state">No guest chats yet.</div>
                  )}
                </div>
              </div>
            </section>

            <section className="admin-panel admin-inquiry-panel" id="admin-inquiries" aria-labelledby="admin-inquiries-title">
              <div className="admin-panel__heading">
                <div>
                  <p className="eyebrow">Guest support</p>
                  <h2 id="admin-inquiries-title">Contact inquiries</h2>
                </div>
                <span className="admin-email-status admin-email-status--sent">
                  {overview.contactInquiries.length} recent
                </span>
              </div>
              <div className="admin-inquiry-list">
                {overview.contactInquiries.slice(0, 6).map((inquiry) => (
                  <article key={inquiry.id}>
                    <div className="admin-inquiry-list__header">
                      <div>
                        <strong>{inquiry.subject}</strong>
                        <span>
                          {inquiry.name} · <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                          {inquiry.phone && (
                            <>
                              {" "}· <a href={`tel:${inquiry.phone.replace(/\D/g, "")}`}>{inquiry.phone}</a>
                            </>
                          )}
                        </span>
                      </div>
                      <small>{formatDateTime(inquiry.createdAt)}</small>
                    </div>
                    <p>{inquiry.message}</p>
                    {inquiry.marketingOptIn && <span className="admin-mini-pill">Marketing opt-in</span>}
                  </article>
                ))}
                {overview.contactInquiries.length === 0 && <div className="admin-empty-state">No contact inquiries yet.</div>}
              </div>
            </section>

            <section className="admin-panel admin-email-diagnostics" id="admin-email" aria-labelledby="admin-email-delivery-title">
              <div className="admin-panel__heading">
                <div>
                  <p className="eyebrow">Delivery readiness</p>
                  <h2 id="admin-email-delivery-title">Email setup</h2>
                </div>
                <span className={`admin-email-status admin-email-status--${overview.emailDelivery.configured ? "sent" : "skipped"}`}>
                  {overview.emailDelivery.configured ? "SMTP ready" : "Logging only"}
                </span>
              </div>
              <div className="admin-email-diagnostics__grid">
                <div>
                  <span>Mode</span>
                  <strong>{overview.emailDelivery.mode}</strong>
                </div>
                <div>
                  <span>Host</span>
                  <strong>{overview.emailDelivery.host || "Not configured"}</strong>
                </div>
                <div>
                  <span>From</span>
                  <strong>{overview.emailDelivery.from}</strong>
                </div>
                <div>
                  <span>Owner inbox</span>
                  <strong>{overview.emailDelivery.adminEmail || "Not configured"}</strong>
                </div>
              </div>
              {overview.emailDelivery.missing.length > 0 && (
                <div className="admin-delivery-warning">
                  <strong>Missing production settings:</strong>
                  <span>{overview.emailDelivery.missing.join(", ")}</span>
                </div>
              )}
              <form className="admin-test-email-form" onSubmit={sendTestEmail}>
                <label>
                  <span>Test recipient</span>
                  <input type="email" maxLength={254} value={testEmail} onChange={(event) => setTestEmail(event.target.value)} placeholder="owner@example.com" required />
                </label>
                <label>
                  <span>Name</span>
                  <input maxLength={120} value={testName} onChange={(event) => setTestName(event.target.value)} placeholder="Optional" />
                </label>
                <button className="button button--secondary" type="submit" disabled={Boolean(actionId)}>
                  <Send size={18} aria-hidden="true" />
                  {actionId === "email:test" ? "Testing" : "Send test email"}
                </button>
              </form>
            </section>

            <section className="admin-panel" id="admin-bookings" aria-labelledby="admin-bookings-title">
              <div className="admin-panel__heading">
                <div>
                  <p className="eyebrow">Reservations</p>
                  <h2 id="admin-bookings-title">Booking requests</h2>
                </div>
                <div className="admin-filter-bar" aria-label="Booking status filter">
                  {(["all", "hold", "confirmed", "cancelled"] as StatusFilter[]).map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={statusFilter === status ? "filter-button active" : "filter-button"}
                      onClick={() => onStatusFilterChange(status)}
                      disabled={loading}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-booking-list">
                {overview.bookings.map((booking) => {
                  const pendingAction = pendingStatusAction?.bookingId === booking.id ? bookingStatusAction(pendingStatusAction.status) : null;

                  return (
                    <article className="admin-booking-card" key={booking.id}>
                      <div className="admin-booking-card__main">
                        <div className="admin-booking-card__meta">
                          <span className={`account-pill account-pill--${booking.status}`}>{statusText(booking)}</span>
                          <span className="admin-reference-pill">Ref {bookingReference(booking)}</span>
                        </div>
                        <h3>{booking.apartmentTitle}</h3>
                        <p>
                          {formatDate(booking.start)} to {formatDate(booking.end)} · {booking.guests} guest{booking.guests === 1 ? "" : "s"} · {money(booking.total)}
                        </p>
                        <p>
                          {booking.guestName} · <a href={`mailto:${booking.guestEmail}`}>{booking.guestEmail}</a> ·{" "}
                          <a href={`tel:${booking.guestPhone.replace(/\D/g, "")}`}>{booking.guestPhone}</a>
                        </p>
                        {booking.note && <p className="admin-booking-note">{booking.note}</p>}
                        {booking.marketingOptIn && <span className="admin-mini-pill">Marketing opt-in</span>}
                        {booking.accountId && <span className="admin-mini-pill">Saved account</span>}
                      </div>
                      <div className="admin-booking-card__actions">
                        {booking.status !== "confirmed" && (
                          <button
                            className="button button--primary"
                            type="button"
                            onClick={() => setPendingStatusAction({ bookingId: booking.id, status: "confirmed" })}
                            disabled={Boolean(actionId)}
                          >
                            <Check size={17} aria-hidden="true" />
                            Confirm
                          </button>
                        )}
                        {booking.status !== "cancelled" && (
                          <button
                            className="button button--light"
                            type="button"
                            onClick={() => setPendingStatusAction({ bookingId: booking.id, status: "cancelled" })}
                            disabled={Boolean(actionId)}
                          >
                            <X size={17} aria-hidden="true" />
                            Cancel
                          </button>
                        )}
                        {booking.status === "cancelled" && (
                          <button
                            className="button button--secondary"
                            type="button"
                            onClick={() => setPendingStatusAction({ bookingId: booking.id, status: "hold" })}
                            disabled={Boolean(actionId)}
                          >
                            <Clock size={17} aria-hidden="true" />
                            Restore hold
                          </button>
                        )}
                        <Link className="text-link" to={`/apartments/${booking.apartmentSlug}`}>
                          View stay
                        </Link>
                      </div>
                      {pendingAction && pendingStatusAction && (
                        <div className="admin-status-confirmation" role="alert">
                          <AlertTriangle size={20} aria-hidden="true" />
                          <div>
                            <strong>{pendingAction.title} </strong>
                            <span>{pendingAction.body} </span>
                          </div>
                          <div className="admin-status-confirmation__actions">
                            <button
                              className="button button--primary"
                              type="button"
                              disabled={Boolean(actionId)}
                              onClick={() => updateBookingStatus(booking, pendingStatusAction.status)}
                            >
                              {actionId === `${booking.id}:${pendingStatusAction.status}` ? "Saving" : pendingAction.button}
                            </button>
                            <button className="button button--light" type="button" disabled={Boolean(actionId)} onClick={() => setPendingStatusAction(null)}>
                              Keep current status
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
                {overview.bookings.length === 0 && <div className="admin-empty-state">No bookings match this filter.</div>}
              </div>
            </section>

            <div className="admin-secondary-grid">
              <section className="admin-panel" id="admin-blocks" aria-labelledby="admin-blocks-title">
                <div className="admin-panel__heading">
                  <div>
                    <p className="eyebrow">Inventory</p>
                    <h2 id="admin-blocks-title">Blocked dates</h2>
                  </div>
                </div>
                <form className="admin-block-form" onSubmit={createBlockedRange}>
                  <label>
                    <span>Apartment</span>
                    <select value={blockApartment} onChange={(event) => setBlockApartment(event.target.value)} required>
                      {bookableApartments.map((apartment) => (
                        <option key={apartment.slug} value={apartment.slug}>
                          {apartment.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Start</span>
                    <input
                      type="date"
                      min={todayDate()}
                      value={blockStart}
                      onChange={(event) => {
                        setBlockStart(event.target.value);
                        if (event.target.value && blockEnd <= event.target.value) {
                          setBlockEnd(addDays(event.target.value, 1));
                        }
                      }}
                      required
                    />
                  </label>
                  <label>
                    <span>End</span>
                    <input type="date" min={blockStart ? addDays(blockStart, 1) : todayDate()} value={blockEnd} onChange={(event) => setBlockEnd(event.target.value)} required />
                  </label>
                  <label className="admin-block-form__full">
                    <span>Reason</span>
                    <input maxLength={160} value={blockReason} onChange={(event) => setBlockReason(event.target.value)} placeholder="Maintenance, owner stay, direct booking" />
                  </label>
                  <button className="button button--secondary button--full" type="submit" disabled={Boolean(actionId)}>
                    <CalendarOff size={18} aria-hidden="true" />
                    {actionId === "block:create" ? "Blocking" : "Block dates"}
                  </button>
                </form>
                <div className="admin-block-list">
                  {overview.blockedRanges.map((range) => (
                    <article key={range.id}>
                      <div>
                        <strong>{range.apartmentTitle}</strong>
                        <span>
                          {formatDate(range.start)} to {formatDate(range.end)}
                        </span>
                        {range.reason && <small>{range.reason}</small>}
                      </div>
                      <button className="icon-button" type="button" onClick={() => removeBlockedRange(range)} aria-label="Remove blocked dates" disabled={Boolean(actionId)}>
                        <X size={18} aria-hidden="true" />
                      </button>
                    </article>
                  ))}
                  {overview.blockedRanges.length === 0 && <div className="admin-empty-state">No upcoming blocked dates.</div>}
                </div>
              </section>

              <section className="admin-panel" id="admin-marketing" aria-labelledby="admin-marketing-title">
                <div className="admin-panel__heading">
                  <div>
                    <p className="eyebrow">Marketing</p>
                    <h2 id="admin-marketing-title">Discount emails</h2>
                  </div>
                  <Download size={22} aria-hidden="true" />
                </div>

                <form className="admin-campaign-form" onSubmit={sendDiscountCampaign}>
                  <label>
                    <span>Campaign</span>
                    <input maxLength={120} value={campaignTitle} onChange={(event) => setCampaignTitle(event.target.value)} required />
                  </label>
                  <label>
                    <span>Discount code</span>
                    <input maxLength={40} value={campaignCode} onChange={(event) => setCampaignCode(event.target.value.toUpperCase())} required />
                  </label>
                  <label className="admin-campaign-form__full">
                    <span>Subject</span>
                    <input maxLength={160} value={campaignSubject} onChange={(event) => setCampaignSubject(event.target.value)} required />
                  </label>
                  <label className="admin-campaign-form__full">
                    <span>Message</span>
                    <textarea maxLength={1800} rows={5} value={campaignMessage} onChange={(event) => setCampaignMessage(event.target.value)} required />
                  </label>
                  <button className="button button--primary button--full" type="submit" disabled={Boolean(actionId) || overview.marketingContacts.length === 0}>
                    <Send size={18} aria-hidden="true" />
                    {actionId === "campaign:send" ? "Sending" : `Send to ${overview.marketingContacts.length} opt-in${overview.marketingContacts.length === 1 ? "" : "s"}`}
                  </button>
                </form>

                <h3 className="admin-subheading">Export contacts</h3>
                <textarea className="admin-export-box" value={marketingExport} readOnly rows={8} aria-label="Marketing email export" />

                <h3 className="admin-subheading">Recent campaigns</h3>
                <div className="admin-campaign-list">
                  {overview.discountCampaigns.slice(0, 5).map((campaign) => (
                    <article key={campaign.id}>
                      <div>
                        <strong>{campaign.title}</strong>
                        <span>
                          {campaign.discountCode} · {campaign.audienceCount} contact{campaign.audienceCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <small>
                        {campaign.sentCount} sent · {campaign.skippedCount} skipped · {campaign.failedCount} failed
                      </small>
                    </article>
                  ))}
                  {overview.discountCampaigns.length === 0 && <div className="admin-empty-state">No discount campaigns sent yet.</div>}
                </div>

                <h3 className="admin-subheading">Recent email activity</h3>
                <div className="admin-email-event-list">
                  {overview.emailEvents.slice(0, 8).map((event) => (
                    <article key={event.id}>
                      <span className={`admin-email-status admin-email-status--${event.status}`}>{event.status}</span>
                      <div>
                        <strong>{event.subject}</strong>
                        <span>{event.recipientEmail}</span>
                        <small>{emailEventDetail(event)}</small>
                        {event.errorMessage && <small>{event.errorMessage}</small>}
                      </div>
                    </article>
                  ))}
                  {overview.emailEvents.length === 0 && <div className="admin-empty-state">No email activity yet.</div>}
                </div>

                <h3 className="admin-subheading">Opted-in contacts</h3>
                <div className="admin-contact-list">
                  {overview.marketingContacts.slice(0, 8).map((contact) => (
                    <div key={contact.email}>
                      <strong>{contact.email}</strong>
                      <span>
                        {contact.name || "Guest"} · {contact.source}
                      </span>
                    </div>
                  ))}
                  {overview.marketingContacts.length === 0 && <div className="admin-empty-state">No opted-in contacts yet.</div>}
                </div>
              </section>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
